import { memo, useContext, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { OEContext } from '@aytm/app/stats/page/comments'

import { range, getToolBoxOptions } from '@aytm/app/stats/page/comments/lib/utils'

import Response from '@aytm/app/stats/page/comments/comments/Response'

import { Button } from 'primereact/button'

import scrollToElement from 'scroll-to-element'

import style from '@aytm/app/stats/page/comments/styles/index.module.scss'

const PageDataComments = memo(
  ({
    reportId,
    apiKey,
    survey,
    responsesOptions,
    responsesRef,
    showTraitsRef,
    responseOrderRef,
    currentResponseRef,
    isVirtualOrXpertInScope,
    isOnlyVirtualOrXpert,
    isShowHiddenFilters,
    popupNotificationsDisabled,
    setCurrentResponse,
    onCloseNotification,
    onUpdateSchema,
    onUpdateParams,
    onPageChange
  }) => {
    const { searchState } = useContext(OEContext)

    const { request, tagsList, totalPages, isLoading, isProcessing, isQAMode, isShowAllTools, isVideoResponsesMode } =
      searchState
    const { currentPage } = request

    const scrollToElementTimerRef = useRef(null)

    const searchStateRef = useRef()
    searchStateRef.current = searchState

    const toolBoxOptions = useMemo(() => getToolBoxOptions(survey, tagsList) || [], [survey, tagsList])

    const trackCurrentResponse = useCallback(() => {
      const bodyPosition = document.body.getBoundingClientRect()
      if (0 < bodyPosition.top) setCurrentResponse(null)
      else {
        for (const responseRef of responsesRef.current) {
          if (!responseRef || null === responseRef.node) continue
          const position = responseRef.node.getBoundingClientRect()
          if (position.top >= 150 || position.top + position.height > bodyPosition.height) {
            setCurrentResponse({ uid: responseRef.uid, index: responseRef.index })
            break
          }
        }
      }
    }, [setCurrentResponse, responsesRef])

    useEffect(() => {
      document.removeEventListener('scroll', trackCurrentResponse)

      if (isLoading || isProcessing) return

      document.addEventListener('scroll', trackCurrentResponse)

      return () => document.removeEventListener('scroll', trackCurrentResponse)
    }, [isLoading, isProcessing, trackCurrentResponse])

    useEffect(() => {
      if (searchStateRef.current.isLoading || searchStateRef.current.isProcessing) return

      scrollToElementTimerRef.current && clearTimeout(scrollToElementTimerRef.current)

      setCurrentResponse(null)
      return () => scrollToElementTimerRef.current && clearTimeout(scrollToElementTimerRef.current)
    }, [apiKey, request, setCurrentResponse])

    useEffect(() => {
      if (searchStateRef.current.isLoading || isProcessing) return

      scrollToElementTimerRef.current && clearTimeout(scrollToElementTimerRef.current)

      if (currentResponseRef?.current) {
        const currentResponseNode = (responsesRef.current || []).find(
          item => item.uid == currentResponseRef.current.uid
        )
        scrollToElementTimerRef.current = setTimeout(() => {
          currentResponseNode
            ? scrollToElement(currentResponseNode.node, {
                offset: -150,
                align: 'top',
                duration: 250
              })
            : trackCurrentResponse()
        }, 100)
      }

      return () => scrollToElementTimerRef.current && clearTimeout(scrollToElementTimerRef.current)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [responsesRef, isProcessing, isShowAllTools, trackCurrentResponse])

    useEffect(() => {
      if (!isLoading) return

      scrollToElement('.-header', {
        offset: 0,
        align: 'top',
        duration: 250
      })
    }, [isLoading])

    return (
      <>
        <div className={style['responses']}>
          {responsesOptions.pageData && 0 < responsesOptions.pageData.length ? (
            responsesOptions.pageData.map((item, i) => (
              <Response
                key={`PageDataResponse_${item.response.uid}`}
                ref={el => (responsesRef.current[i] = { uid: item.response.uid, node: el, index: i })}
                dataId={`Response_${item.response.uid}`}
                reportId={reportId}
                survey={responsesOptions.survey}
                reportVirtual={responsesOptions.reportVirtual}
                reportSchema={responsesOptions.reportSchema}
                response={item}
                toolBoxOptions={toolBoxOptions}
                showTraitsRef={showTraitsRef}
                responseOrderRef={responseOrderRef}
                isVirtualOrXpertInScope={isVirtualOrXpertInScope}
                isOnlyVirtualOrXpert={isOnlyVirtualOrXpert}
                popupNotificationsDisabled={popupNotificationsDisabled}
                isShowHiddenFilters={isShowHiddenFilters}
                onUpdateSchema={onUpdateSchema}
                onUpdateParams={onUpdateParams}
                onCloseNotification={onCloseNotification}
              />
            ))
          ) : (
            <div className={style['no-results']}>
              {!isLoading && <>{isVideoResponsesMode ? 'No videos found' : 'No comments found'}</>}
            </div>
          )}
          {(isLoading || isProcessing) && (
            <div
              className={classNames(style['status-loader'], {
                [style['status-loader-static']]: !responsesOptions.pageData || !responsesOptions.pageData.length
              })}
            >
              <span className={`afd-loader afd-loader-long`}>
                {range(0, 80).map(id => (
                  <span key={`loading-span-${id}`} />
                ))}
              </span>
            </div>
          )}
        </div>
        {!isQAMode && !isLoading && !isProcessing ? (
          <>
            <div className={style['responses-bottom-paginaton']}>
              <div className={style['left']}>
                {1 < currentPage ? (
                  <Button
                    className="p-button-rounded p-button-blue"
                    label="PREVIOUS PAGE"
                    disabled={isLoading || isProcessing}
                    onClick={isLoading || isProcessing ? null : () => onPageChange(currentPage - 1, null)}
                  />
                ) : null}
              </div>
              <div className={style['right']}>
                {currentPage < totalPages ? (
                  <Button
                    className="p-button-rounded p-button-blue"
                    label="NEXT PAGE"
                    disabled={isLoading || isProcessing}
                    onClick={isLoading || isProcessing ? null : () => onPageChange(currentPage + 1, null)}
                  />
                ) : null}
              </div>
            </div>
          </>
        ) : null}
      </>
    )
  }
)

PageDataComments.propTypes = {
  reportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  apiKey: PropTypes.object,
  survey: PropTypes.object,
  responsesOptions: PropTypes.object,
  responsesRef: PropTypes.object,
  showTraitsRef: PropTypes.object,
  responseOrderRef: PropTypes.object,
  currentResponseRef: PropTypes.object,
  isVirtualOrXpertInScope: PropTypes.bool,
  isOnlyVirtualOrXpert: PropTypes.bool,
  isShowHiddenFilters: PropTypes.bool,
  popupNotificationsDisabled: PropTypes.object,
  setCurrentResponse: PropTypes.func,
  onCloseNotification: PropTypes.func,
  onUpdateSchema: PropTypes.func,
  onUpdateParams: PropTypes.func,
  onPageChange: PropTypes.func
}

PageDataComments.displayName = 'PageDataComments'

export default PageDataComments
